import { useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthProvider';
import { useUserQuery } from '@/api/user/useUserQuery';
import { identifyUser, resetUser } from '@/mixpanel';
import type { User } from '@/api/user/types';

function buildUserTraits(user: User): Record<string, string> {
  const traits: Record<string, string> = {};
  traits.$email = user.email;
  if (user.role) traits.role = user.role;
  if (user.currentPlan?.tier) traits.planTier = user.currentPlan.tier;
  if (user.organization?.id) traits.organizationId = user.organization.id;
  if (user.organization?.name) traits.organizationName = user.organization.name;
  return traits;
}

export function useMixpanelIdentify() {
  const { isAuthenticated } = useAuth();
  const { data: user, isLoading: isUserLoading } = useUserQuery();
  const lastIdentifiedUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    // If user is logged out, reset Mixpanel user tracking and clear the last identified user reference
    if (!isAuthenticated) {
      if (lastIdentifiedUserIdRef.current !== null) {
        resetUser();
        lastIdentifiedUserIdRef.current = null;
      }
      return;
    }

    if (isUserLoading || !user) return;

    // If the user is already identified and has the same user ID, return to avoid duplicate identification
    if (lastIdentifiedUserIdRef.current === user.userId) return;

    const traits = buildUserTraits(user);
    identifyUser(user.userId, traits);
    lastIdentifiedUserIdRef.current = user.userId;
  }, [isAuthenticated, user, isUserLoading]);
}
